import Linkify from "linkify-react";
import { emojiRegex, emojis } from "../../emojis/emojis";

// --- Helper Function ---
function isDirectImageUrl(url: string): boolean {
  if (!url) {
    return false;
  }
  // Basic check for common image extensions
  return /\.(?:png|jpg|jpeg|gif|webp|svg|bmp)(?:\?.*|#.*)?$/i.test(url);
}

interface RenderMessageContentProps {
  text: string;
}

export function MessageContent({ text }: RenderMessageContentProps) {
  const parts: (string | React.ReactElement)[] = [];
  let lastIndex = 0;
  let match;

  // Reset regex state before each use
  emojiRegex.lastIndex = 0;

  // Iterate through emoji matches
  while ((match = emojiRegex.exec(text)) !== null) {
    const emojiCode = match[1]?.toLowerCase();
    if (!emojiCode) {
      continue; // Skip if no emoji code found
    }
    console.log("Emoji code:", emojiCode); // Debugging log
    const emojiSlug = emojis[emojiCode];
    const startIndex = match.index;
    const endIndex = emojiRegex.lastIndex;
    console.log("Emoji slug:", emojiSlug); // Debugging log

    // Add preceding text segment (if any)
    if (startIndex > lastIndex) {
      parts.push(text.substring(lastIndex, startIndex));
    }

    // Add emoji image or the original code if not found
    if (emojiSlug) {
      parts.push(
        <img
          key={startIndex} // Use index as key for stability within the message
          src={`/emojis/${emojiSlug}`}
          alt={`:${emojiCode}:`}
          title={`:${emojiCode}:`}
          className="inline-block h-5 w-5 align-text-bottom mx-px" // Style emoji
        />
      );
    } else {
      // Emoji code not found in map, render as text
      parts.push(match[0]);
    }

    lastIndex = endIndex;
  }

  // Add remaining text segment (if any)
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  // Now, process the parts array, linkifying only the string segments
  return (
    <span className="content break-words text-sm text-slate-800">
      {parts.map((part, index) => {
        if (typeof part === "string") {
          // This is a text segment, wrap it with Linkify
          return (
            <Linkify
              key={index} // Use index as key
              options={{
                // Linkify options for text links and image links within text
                render: ({ attributes, content }) => {
                  const { href, ...props } = attributes;
                  if (isDirectImageUrl(href)) {
                    // Render image links found within text segments
                    return (
                        <img
                          src={href}
                          alt={`Linked image: ${content}`}
                          className="mt-1 block max-w-xs max-h-36 rounded-md sm:max-w-sm sm:max-h-48 hover:opacity-90 transition-opacity"
                          onError={(e) =>
                            console.warn(`Failed to load image: ${href}`)
                          }
                        />
                    );
                  }
                  // Render standard text links
                  return (
                    <a
                      href={href}
                      {...props}
                      className="text-indigo-600 hover:text-indigo-800 hover:underline"
                    >
                      {content}
                    </a>
                  );
                },
                target: "_blank",
                rel: "noopener noreferrer",
              }}
              tagName="span"
            >
              {part}
            </Linkify>
          );
        } else {
          return part; // Render the emoji image directly
        }
      })}
    </span>
  );
}
